import { createStage } from './core/stage.js';
import { createLoop } from './core/loop.js';
import { createFight } from './core/fight.js';
import { frameCamera } from './core/framing.js';
import { createInput } from './core/input.js';
import { createPlayerControl } from './core/playerControl.js';
import { createFighterAI } from './core/fighterAI.js';
import { difficultyFor, tierNames } from './core/difficulty.js';
import { LADDER, createLadder } from './sim/ladder.js';
import { validateVenues } from './sim/venues.js';
import { createCampaign } from './sim/campaign.js';
import { bookFighter } from './sim/booking.js';
import { createSaveStore } from './sim/saveStore.js';
import { createBank, looksOf } from './sim/bank.js';
import { createImpact } from './core/impact.js';
import { createDirector } from './core/director.js';
import { createMatch } from './core/match.js';
import bindings from './core/bindings.json';
import { createArenaHost, DEFAULT_ARENA, finishName, places } from './arenas/index.js';
import { createCrowd } from './core/crowd.js';
import { createAudio } from './core/audio.js';
import { createWebAudioDevice } from './core/audioDevice.js';
import { createWeapon, createWeaponGround, weaponIds, WEAPONS } from './core/weapons.js';
import { createWeaponDrops } from './fighters/weaponModel.js';
import { createCombatant } from './fighters/combatant.js';
import { createDebugOverlay } from './ui/debugOverlay.js';
import { createWalkout } from './ui/walkout.js';
import { createHub } from './ui/hub.js';
import { createHud } from './ui/hud.js';
import { front } from './front.js';
import { createPause } from './ui/pause.js';
import { createAftermath } from './ui/aftermath.js';
import { MESHES } from './fighters/meshes.js';

const asset = (path) => `${import.meta.env.BASE_URL}${path}`;
/**
 * Both corners wear fighter-a — the one real body there is — and are told
 * apart by colour, which comes off each roster entry through `bookFighter`.
 * The mannequin is the fallback: it ships with the game and has the same
 * skeleton, so a fighter-a that fails to load degrades the picture to the old
 * one rather than blanking it, and says so in the console.
 */
const MODEL = asset(`models/fighters/${MESHES.default}`);
/**
 * The player's own charisma, before anything is worn. Gear multiplies it
 * (`bank.charisma().scale`), and a crowd bored of the look takes the gear's
 * share back — see `wearWhatWasBought`.
 */
const PLAYER_CHARISMA = 1.2;
const FALLBACK_MODEL = asset('models/placeholder-fighter.glb');
/**
 * A file from `meshes.json` as a URL. Each corner starts in the default body
 * and changes into the booked fighter's own when they are booked (`wear`);
 * Big Emeka walks out as fighter-b.
 */
const meshUrl = (file) => asset(`models/fighters/${file}`);

/**
 * The Mixamo library, one clip per file. Each name is derived from the part of
 * the filename after the "@" — "X Bot@Lead Jab.fbx" registers as `leadJab` —
 * and that is the name `states.json` and `moves.json` ask for.
 *
 * The spaces have to be escaped to survive into a URL, but the "@" must not
 * be: static hosts serve these straight off disk and a `%40` misses the file,
 * where a literal `@` finds it. That is `encodeURI`'s rule, not
 * `encodeURIComponent`'s.
 */
const CLIPS = [
  'X Bot@Lead Jab.fbx',
  'X Bot@Jab Cross.fbx',
  'X Bot@Hook.fbx',
  'X Bot@Uppercut.fbx',
  'X Bot@Roundhouse Kick.fbx',
  'X Bot@Mma Kick.fbx',
  'X Bot@Pontera.fbx',
  'X Bot@Body Block.fbx',
  'X Bot@Fighting Idle.fbx',
  'X Bot@Walking.fbx',
  'X Bot@Walking Backwards.fbx',
  'X Bot@Getting Up.fbx',
  'X Bot@Knocked Out.fbx',
  'X Bot@Taunt.fbx',
  'X Bot@Receive Uppercut To The Face.fbx',
  'X Bot@Receive Stomach Uppercut.fbx',
];

/**
 * The placeholder pack still loads, and still first: the grapple states have
 * no Mixamo clip of their own yet and fall back to its `strike`, and loading
 * it ahead of the real library means any name the two share is won by the real
 * one.
 */
const ANIMATIONS = [
  asset('models/placeholder-anims.glb'),
  // The sixteen clips in `CLIPS`, packed into one file by
  // `scripts/bake-clips.mjs` (step 95): 8 MB of FBX became 149 kB on the wire.
  // `CLIPS` stays as the list of what the library is; the FBX files are the
  // source the pack is baked from, and the game never fetches them.
  asset('models/clips.json'),
];

const stage = createStage(document.getElementById('viewport'));

/**
 * Where the fighters stand before an arena has said otherwise.
 *
 * They are built here and put on their marks by `arenas.load()` a few lines
 * further down, because the marks are the arena's to decide and the arena is
 * not loaded yet. Nobody sees this position — the first load happens before the
 * first frame.
 */
const ORIGIN = [0, 0, 0];

const [player, opponent] = await Promise.all([
  createCombatant({
    id: 'player',
    modelUrl: MODEL,
    fallbackUrl: FALLBACK_MODEL,
    meshUrl,
    defaultMesh: MESHES.default,
    animationUrls: ANIMATIONS,
    position: ORIGIN,
    // Deliberately different, so the stat is visible rather than theoretical:
    // the same punches fill these two meters at noticeably different rates.
    charisma: PLAYER_CHARISMA,
    // Swappable at runtime from the debug overlay. The primary is what decides
    // how this fighter is allowed to win.
    styles: ['streetfighting'],
  }),
  createCombatant({
    id: 'opponent',
    modelUrl: MODEL,
    fallbackUrl: FALLBACK_MODEL,
    meshUrl,
    defaultMesh: MESHES.default,
    animationUrls: ANIMATIONS,
    position: ORIGIN,
    charisma: 0.8,
    styles: ['kickboxing', 'wrestling'],
  }),
]);

// Squared up, each facing the other, so either one's punch travels at a body.
player.faceTowards(opponent.position);
opponent.faceTowards(player.position);
// And told who that is. Only heat asks, so that every event in its list can
// name the fighter it happened against.
player.squareUpWith(opponent);
opponent.squareUpWith(player);

for (const combatant of [player, opponent]) {
  stage.scene.add(combatant.object3D, ...combatant.volumeViews);
}

// Empty to start with. The arena fills all of this in when it loads, and it is
// the one place that knows how — see `src/arenas/index.js`.
const fight = createFight();
fight.add(player, opponent);

const impact = createImpact();
fight.onHit((hit) => impact.hit(hit));
// The camera's own moves (step 98): drift, and the push and hold on a finish.
const director = createDirector();
fight.onKnockout(({ loser }) => {
  const body = fight.combatants.find((c) => c.id === loser);
  director.knockout(body?.position ?? null);
});

const match = createMatch({ fight, fighters: [player, opponent] });
// The director lets go of a held finish when the next round's intro starts.
match.onPhase(({ to }) => {
  if (to === 'intro') director.reset();
});

/**
 * The room. It watches both fighters and reacts to whichever of them is worth
 * watching — a crowd does not average two people, and a fight where one man is
 * electric and the other is dull is not a quiet night.
 */
/**
 * The floor, as far as weapons are concerned: what is lying on it, and who is
 * standing close enough to pick it up.
 *
 * Its `bounds` come from the arena, like everything else an arena decides, so
 * nothing the crowd throws in lands inside the tiling.
 */
const ground = createWeaponGround();
const drops = createWeaponDrops(ground, { config: WEAPONS });
stage.scene.add(drops.object3D);

const crowd = createCrowd({ fighters: [player, opponent], ground });

/**
 * What all of it sounds like.
 *
 * **This is the whole of the integration.** Every hook it uses already existed
 * for the overlay's benefit or for the dev log, so nothing in `core` or `sim`
 * was changed to make a noise possible and nothing in either of them knows this
 * exists. Take these four lines out and the game is silent and otherwise
 * identical.
 *
 * The device is the browser half and is allowed to be missing: no
 * `AudioContext` means a null device, which `audio.js` treats as a silent game
 * rather than a broken one.
 *
 * Up here, above the ladder, because the **save** carries the master volume and
 * the save store is built a few lines down. The three things it is handed that
 * are not the fight — the floor, the room and the two fighters — are all pure
 * and were only ever constructed further down out of habit.
 */
const audio = createAudio({
  device: createWebAudioDevice({ onWarn: (message) => console.warn(`[audio] ${message}`) }),
  fight,
  match,
  crowd,
  ground,
  fighters: [player, opponent],
});

/**
 * The ladder, and the player's run up it.
 *
 * Pure and knows nothing about any of the above — it is handed results and
 * hands back ranks. Everything that connects it to the fight is in this file,
 * which is the only place that knows both that a `match` has a winner and that
 * a `campaign` wants to be told about one.
 */
/**
 * Every venue is fought in an arena that is registered, and every arena is
 * somebody's venue. `src/sim` cannot ask — it imports no three.js, and the
 * registry builds scenes — so the one place that holds both asks here, at
 * boot, and a card naming a room that does not exist fails before anybody is
 * offered it. `venues.test.js` asks the same question in the suite.
 */
validateVenues({ venues: LADDER.venues }, { roster: LADDER.roster, arenas: places() });

const ladder = createLadder();
const campaign = createCampaign({
  ladder,
  // The purse is partly what the player is worth to watch, and heat is where
  // that already lives. Read through a function rather than handed over as a
  // number, because it moves all round and an offer made at the bell would be
  // priced on a fighter who no longer exists.
  heat: () => player.heat?.value ?? undefined,
});

/**
 * The two currencies, and the one thing in this file that has to hold both.
 *
 * Subscribed to `campaign.onRecord` **before** the save is, because listeners
 * fire in the order they were added and a career written before the purse
 * landed is a career one fight behind its own money.
 */
const bank = createBank();
campaign.onRecord((event) => {
  const earned = bank.settle(event);
  if (import.meta.env.DEV && earned) {
    console.info(
      `[bank] +$${earned.purse ?? earned.cash}${earned.rooms ? ` · +$${earned.rooms} from rooms` : ''}` +
        `${earned.points ? ` · +${earned.points} pts` : ''}` +
        ` · $${bank.cash} / ${bank.points} pts`
    );
  }
});

match.onPhase(({ to, winner, finishedBy, verdict, on }) => {
  if (to === 'over' && verdict) {
    // A loss is recorded exactly like a win. There is no branch here for one,
    // and there is not going to be: the campaign has no failure state.
    //
    // The heat list goes *with* it. Heat is a per-round thing and `match.reset`
    // wipes it, so the only moment it can be read is this one — and both
    // screens want it afterwards: the aftermath to show what the crowd saw,
    // and the next walkout to let the opponent mention it.
    campaign.record({
      won: winner === player.id,
      // A draw has no winner, so the third outcome cannot be inferred from
      // `won` — every reader of a verdict has to be told which of the three it
      // is rather than guessing from the absence of the other two.
      drawn: verdict === 'draw',
      by: verdict,
      method: finishedBy,
      on,
      heat: player.heat?.events ?? [],
      // Where it was fought. The card's venue — unless an arena was picked by
      // hand and the fight was somewhere else entirely, in which case no room
      // is won or lost in a place nobody was standing.
      at: arenas.pinned && arenas.id !== campaign.next.venue?.arena ? false : null,
    });
    return;
  }
  // The bell. A look the crowd has seen too often lately costs heat — the
  // same boredom that is fading the gear's charisma, said in the currency the
  // room already keeps (`bank.boredom`, `stale` in `heat.json`).
  if (to === 'fighting' && bank.boredom.stale) player.heat?.record('stale');
  // Whoever holds the room has the room behind them: one `home`
  // event, once, and then it drifts back like any other heat (`venues.js`).
  if (to === 'fighting') {
    const venue = campaign.next.venue;
    if (!venue || arenas.pinned || arenas.id !== venue.arena) return;
    const holder = ladder.venue(venue.id)?.holder;
    const booked = campaign.next.opponent?.id;
    if (holder === ladder.playerId) player.heat?.record('home');
    else if (holder && holder === booked && opponent.name === campaign.next.opponent?.name) {
      opponent.heat?.record('home');
    }
    return;
  }
  // Back on the marks, and the hub goes up.
  //
  // `book` runs first and runs *again* when a card is taken. Twice on purpose:
  // dressing a fighter is idempotent, and a caller that never touches the offer
  // — the headless harness, which goes straight through `match.start()` — still
  // gets whoever the campaign is pointing at, exactly as it did before there
  // were three of them.
  //
  // The round is held from here until the walkout is dismissed. `match.hold`
  // rather than a stopped frame, for the reason in gotcha 55.
  if (to === 'intro') {
    // Nothing goes up over the front end. The title resets the career on its
    // way out, which fires this, and a hub appearing behind the title screen is
    // the one way the boot order can go wrong.
    if (!started) return;
    book();
    warmTheCards();
    match.hold(true);
    // If there is nothing to offer — a roster too small to make three different
    // cards out of — the walkout goes up instead. A held round with no screen
    // over it is a game nobody can leave (gotcha 55).
    if (!hub?.show()) walkout?.show();
  }
});

/**
 * The career, kept across reloads.
 *
 * Written on the moments that change it — a fight recorded, a new round booked
 * — and once more on the way out of the page, which is the case that matters:
 * a reload should continue the career rather than starting a new one. The
 * ladder churns between those points, so a hard kill mid-round can lose a
 * background result or two; the player's own record cannot be lost, because
 * every one of theirs is a save.
 */
const store = createSaveStore({
  ladder,
  campaign,
  player,
  bank,
  audio,
  onWarn: (message) => console.warn(`[career] ${message}`),
});

// A fight of the player's is always worth a write, immediately.
campaign.onRecord(() => store.save());
// Somebody else's result only marks the career dirty: the ladder churns every
// sixteen seconds and writing on each one would be a write a fight-length
// apart for something nobody watched.
ladder.onResult((result) => {
  if (result?.background) store.touch();
});
// Somebody arriving is not a result and does not go through that hook, and it
// is not a thing to lose: the whole point of him is that the player finds him
// behind them, and a hard kill that put him back offstage would quietly undo
// the only moment he has.
ladder.onDebut((who) => {
  if (import.meta.env.DEV) {
    console.info(`[ladder] ${who.name} turns up at rank ${who.rank} (${who.because})`);
  }
  store.save();
});
// Flushed when the next round is booked, which is the natural quiet moment.
match.onPhase(({ to }) => {
  if (started && to === 'intro') store.flush();
});

/**
 * And on the way out, whether or not anything is dirty — this is the write that
 * makes a reload continue the round's *heat* rather than only its result.
 *
 * **Only for a career somebody actually started.** Before the title screen
 * existed this could not go wrong: boot loaded whatever was there, so a save
 * written by an idle page was the same career it would have loaded anyway. Now
 * it is the difference between a front door that offers *continue* to somebody
 * who has never played and one that does not — leaving the page open for a
 * second is not starting a career, and the automatic writes are the only ones
 * that had to learn that. `restartCareer` still writes immediately and on
 * purpose, because a reload straight after a restart must not find the career
 * that was just thrown away.
 */
const writeOut = () => (started ? store.save() : false);
window.addEventListener('beforeunload', writeOut);
// Safari on iOS does not reliably fire `beforeunload`; this is the one it does.
window.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') writeOut();
});

/**
 * A fresh career, on purpose.
 *
 * Deliberate and explicit: nothing a reload can reach. A reload *loads*, and
 * the only thing that ever throws a career away is somebody asking for it — or
 * a save this version cannot read, which is discarded with a reason rather than
 * half-applied. See `save.js`.
 */
function restartCareer() {
  ladder.reset();
  campaign.reset();
  bank.reset();
  // Not the volume. A fresh career is a fresh *career*; somebody who turned the
  // sound off did not ask for it back.
  player.heat?.reset?.();
  dressThePlayer();
  wearWhatWasBought();
  store.clear();
  // Written immediately rather than left until the next fight, so a reload
  // straight after a restart does not find the career that was just thrown out.
  store.save();
  // `reset` is forced rather than entered, so it fires the `intro` phase even
  // from `intro` — and the listener on it is what puts the next screen up. This
  // used to do that itself, which was one caller too many once there was a
  // title screen that also resets and does *not* want a hub over it.
  match.reset();
  return campaign;
}

/**
 * The two screens the game stops for.
 *
 * Created here, straight after the campaign, because the phase listener above
 * shows the walkout and the aftermath subscribes to `campaign.onRecord` — both
 * have to exist before anything can fire into them. Neither touches the scene;
 * they are DOM over the top of it.
 */
/**
 * Whether the player has come through the front door.
 *
 * `false` until the title screen hands over, and read by the phase listener
 * above — everything else about the game is running by then (the ladder churns,
 * the train runs, the crowd watches an empty platform), and only the *screens*
 * have to wait.
 */
let started = false;
/**
 * What the last `store.load()` decided, or null on a career that was started
 * fresh. It used to be a `const` settled at boot; loading is a *door* now
 * rather than a step, so it is written when somebody goes through one.
 */
let loaded = null;

const walkout = createWalkout({
  campaign,
  match,
  bank,
  // The bell waits for the booked fighter's body: a round that started in the
  // default body and changed mid-fight would not be the fight on the card.
  ready: () => opponent.bodyReady,
  // And if a body never arrived at all, the walkout says who is fighting in a
  // stand-in — the mannequin, or the capsule if even that failed — rather than
  // the ring showing it without a word (step 96).
  standIns: () => {
    const who = [
      [player, 'you'],
      [opponent, campaign.next.opponent?.name ?? 'your opponent'],
    ].filter(([fighter]) => !fighter.wearing || !fighter.wearing.includes('/models/fighters/'));
    if (!who.length) return null;
    const many = who.length > 1;
    return `${who.map(([, name]) => name).join(' and ')} ${many ? 'are' : 'is'} fighting as ${many ? 'stand-ins' : 'a stand-in'} — ${many ? 'the bodies' : 'the body'} did not download. Reload to try again.`;
  },
  // A change of clothes at the walkout, the last moment before the bell: put
  // on through the bank like the hub's, and worn at once.
  onWear: () => {
    wearWhatWasBought();
    store.save();
  },
});
// A finish against the room is named by the room: "by a car", not "by carWest".
// `arenas` is built further down; this is only called once a round has ended.
const aftermath = createAftermath({ campaign, match, nameOf: (id) => finishName(arenas.current, id) });

/**
 * The offer, and the one thing it costs that this file has to pay.
 *
 * `campaign.accept` decides whether the reach was ducked once too often; the
 * *heat* is applied here, because heat lives on a fighter and `src/sim` has
 * never held one. The size of it is `heat.json`'s, with the other nine — all
 * this does is fire the event.
 */
const hub = createHub({
  campaign,
  bank,
  styles: () => player.loadout.ids,
  /**
   * Something was bought. The bank has already taken the money; this is the
   * half that reaches a fighter — the spread layer for gear and training, and
   * the loadout for a style, which is the player's own and which the save
   * already carries (gotcha 60).
   */
  onSpend: (event) => {
    if (event.styles) player.setStyles(event.styles);
    wearWhatWasBought();
    store.save();
    if (import.meta.env.DEV) {
      console.info(`[bank] ${event.what} · $${bank.cash} / ${bank.points} pts`);
    }
  },
  onPick: (taken) => {
    if (taken.duck?.charged) player.heat?.record('ducked');
    if (import.meta.env.DEV) {
      console.info(
        `[card] ${taken.slot}: ${taken.opponent.name} · rank ${taken.opponent.rank} · $${taken.purse}` +
          `${taken.duck?.charged ? ' · ducked the reach' : ''}`
      );
    }
    // Re-dressed against the card that was actually taken, then out they go.
    book();
    walkout.show();
  },
});

if (import.meta.env.DEV) {
  campaign.onRecord((event) => {
    console.info(
      `[ladder] ${event.won ? 'beat' : 'lost to'} ${event.name} · rank ${event.from} → ${event.rank}` +
        `${event.gate.open ? ' · contender' : ` · ${event.gate.short} off the card`}`
    );
  });
}

const input = createInput(bindings);
const control = createPlayerControl({ combatant: player, opponent, input, grapple: fight.grapple });

/**
 * The arena, and the ability to change it.
 *
 * Everything an arena decides for somebody else — the walkable floor, the
 * marks, the camera, the collision, the pit, the lights — is re-pointed in one
 * place when it loads, because the way to get a swap wrong is to do four of the
 * five at the call site.
 */
/**
 * Fighter two, driven by something that reads the fight.
 *
 * `opponent:` here is the *player*, because that is who fighter two is squared
 * up against. It is handed the floor as well, so it can see a weapon that has
 * been thrown in — and it is handed no input layer, because the one thing it
 * must never see is what the player is about to do.
 */
/**
 * A tier pinned from the URL, overriding whatever the roster says.
 *
 * `?ai=rookie` and the rest. **The roster decides now** — every fighter on the
 * ladder declares the tier they fight at — so this is a debug affordance, and
 * the headless harness's way of putting two fighters at the *same* difficulty
 * regardless of who is next. A bad name warns and falls back rather than
 * failing to boot: a typo in a query string is not a reason to have no game.
 */
function pinnedTier() {
  const asked = new URLSearchParams(window.location.search).get('ai');
  if (!asked) return null;
  if (tierNames().includes(asked)) return asked;
  console.warn(`[ai] no such difficulty "${asked}"; there is ${tierNames().join(', ')}`);
  return null;
}

const pin = pinnedTier();

const ai = createFighterAI({
  combatant: opponent,
  opponent: player,
  grapple: fight.grapple,
  ground,
  difficulty: pin,
  // The room is pointed at it by `createArenaHost` a few lines down, in the
  // same loop that gives it a floor to walk on. What it *does* with a room
  // comes out of the roster entry, through `book`.
});

/**
 * Whoever the ladder says is next, in the slot the game fights in.
 *
 * The slot is the same combatant all session — the rig, the hitboxes and its
 * place in the scene never move. What changes is who is standing in it, and
 * `bookOpponent` is the one place that knows which parts of a roster entry a
 * fighter wears and which belong to the ladder.
 *
 * The AI is **re-tiered rather than rebuilt**. The overlay holds it, the arena
 * host holds its locomotion and the frame loop calls its update, so replacing
 * it would mean re-pointing three references — and the failure mode of missing
 * one is an overlay reporting on a fighter nobody is driving.
 */
/**
 * The three bodies on the table, fetched while the player is still reading
 * them (step 95). Whichever card they take, its fighter's body is already
 * parsed and retargeted — or on its way — rather than starting its download
 * at the walkout. Cached by file, so a body already seen costs nothing.
 */
function warmTheCards() {
  // One at a time, the card the campaign points at first: on a slow line three
  // bodies at once share the bandwidth three ways, and the one the player takes
  // arrives last instead of first.
  const cards = [...(campaign.offer ?? [])].sort((a, b) => Number(b.pointed) - Number(a.pointed));
  warming = cards.reduce(
    (chain, card) => chain.then(() => opponent.preload(card.opponent?.mesh ?? null)),
    warming.catch(() => {})
  );
}
/** The warm-up chain, so a second hub does not start a second race. */
let warming = Promise.resolve();

function book() {
  const next = campaign.next.opponent;
  const worn = bookFighter(next, { fighter: opponent, brain: ai, pin });
  // The player's look for tonight: the last fight has gone into the crowd's
  // memory, so the same gear may be worth less to them now.
  wearWhatWasBought();
  // Off the city's books while they are on the player's: nobody fights twice
  // in a night, and the room on the line here must not change hands elsewhere.
  ladder.engage(next?.id ?? null);
  // And where. The card names the venue; the venue names the arena; the host
  // swaps it in quietly, inside the intro this is always called from, so the
  // round stays held on its marks under whichever screen is up. A hand-picked
  // arena (the overlay, a harness) is pinned and the card does not move it.
  const venue = campaign.next.venue;
  if (venue) arenas.visit(venue.arena);
  if (import.meta.env.DEV && worn) {
    console.info(
      `[ladder] ${worn.name} walks out · rank ${next.rank} · ${worn.tier}` +
        `${worn.pinned ? ' (pinned)' : ''} · ${worn.styles.join(' + ')}` +
        `${worn.ringcraft ? ` · reads the room (${Object.keys(worn.ringcraft).join(', ')})` : ''}` +
        `${venue ? ` · at ${venue.name} (${arenas.id}${arenas.pinned ? ', pinned' : ''})` : ''}`
    );
  }
  return worn;
}

/**
 * And the player, out of their own roster entry, through the same door.
 *
 * **Once, at boot, rather than every round.** The opponent is re-booked each
 * time because a different person walks out; the player's entry never changes,
 * so re-applying it would do nothing except undo the styles somebody picked in
 * the debug panel every time the round reset. The *path* is shared — one
 * `bookFighter`, one `NOT_WORN` — and only the cadence differs, because only
 * one of the two slots changes hands.
 *
 * Their tier is left behind by `NOT_WORN`, not by this call: nothing is driving
 * the player but a person, and there is no brain in that slot to re-tier.
 */
function dressThePlayer() {
  const worn = bookFighter(ladder.player, { fighter: player });
  if (import.meta.env.DEV && worn) {
    console.info(`[ladder] you are ${worn.name} · ${worn.styles.join(' + ')}`);
  }
  return worn;
}

/**
 * Everything the career has bought, laid over the roster's own spread.
 *
 * Called after the player is dressed, after a career is loaded, and after every
 * purchase. It is not part of `bookFighter` and must not be: booking is what a
 * **roster entry** puts on a fighter, and gear is not in the roster (a field
 * there that nobody wears is the bug the `WORN`/`NOT_WORN` table exists to
 * catch). This is a second layer over the top of it, which is exactly what the
 * bank's `spread` is written to be.
 */
function wearWhatWasBought() {
  player.setSpread(bank.spread(ladder.player.spread));
  // And what the look is worth to the room tonight: the worn tiers' charisma,
  // faded by how often the crowd has seen this exact look lately.
  player.setCharisma(PLAYER_CHARISMA * bank.charisma().scale);
  // And on the body, where the camera can see it: hands, feet, shins.
  player.setGear(looksOf(bank.worn));
  return player.spread;
}

/**
 * The player's side, driven by something other than the player.
 *
 * Null in an ordinary game. Set, it takes the controls off the input layer
 * entirely — which is what lets a headless run put two AIs in with each other
 * and watch the whole thing play out, and what an attract mode would use.
 */
let autopilot = null;

function setAutopilot(difficulty = null) {
  autopilot = difficulty
    ? createFighterAI({
        combatant: player,
        opponent,
        grapple: fight.grapple,
        ground,
        difficulty,
      })
    : null;
  if (autopilot) {
    // The arena decides where anybody may walk and what there is to be put
    // through, and a driver that arrived after the arena did has never been
    // told either.
    autopilot.locomotion.setBounds(arenas.current?.bounds ?? null);
    autopilot.setArena(arenas.current ?? null);
    locomotors.push(autopilot);
  }
  return autopilot;
}

if (import.meta.env.DEV) console.info(`[ai] ${ai.tier}: ${ai.describe}`);

if (import.meta.env.DEV) {
  crowd.onOffer(({ id, forWhom, reason }) => {
    console.info(`[crowd] throws in a ${id} for ${forWhom} (${reason})`);
  });
  ground.onPickup(({ id, by }) => console.info(`[weapon] ${by} picks up the ${id}`));
  crowd.onGrab(({ id, forWhom, reason, held }) => {
    console.info(`[crowd] ${held ? 'grabs' : 'lets go of'} ${id}${held ? ` (${reason}, for ${forWhom})` : ''}`);
  });
}

/**
 * The things with legs to re-floor when the arena changes. Mutable, because an
 * autopilot can arrive later and still has to be told where the floor is.
 */
const locomotors = [control, ai];

const arenas = createArenaHost({
  stage,
  fight,
  match,
  locomotors,
  crowd,
  onWarn: (message) => console.warn(`[arena] ${message}`),
  onChange: (arena) => {
    // Nothing the crowd throws in may land somewhere nobody can walk to.
    ground.setBounds(arena.bounds);
    ground.clear();
    // The train's cues, to the audio layer — which is what the hook was put
    // there for in step 33 and what the comment here promised for twenty-eight
    // build steps. Re-pointed on every swap, like everything else an arena
    // decides for somebody else, because a disposed arena still holding a
    // listener announces departures for a service nobody can see.
    audio.arena(arena);
    arena.train?.onCue(({ cue, seconds }) => {
      if (import.meta.env.DEV) {
        console.info(`[train] ${cue}${seconds ? ` (${seconds.toFixed(1)}s)` : ''}`);
      }
    });
  },
});
// Not `load`, which is the hand-picked door and pins. The first card of the
// career replaces this with wherever it is fought; until then the title screen
// sits over a platform.
arenas.visit(DEFAULT_ARENA);

// The opening round. `match` starts *in* `intro` rather than entering it, so no
// phase change ever fires for the first one — and without this the first
// opponent would be whatever the combatant was built with rather than whoever
// the ladder says is next.
/**
 * The opening: the roster, then the save, then whoever is next.
 *
 * The order is the only one that works and each step is why.
 *
 * `dressThePlayer` first, because the roster entry is the baseline — a career
 * that has never been saved still has to put somebody in the slot. Then the
 * save, which *overrides* the player's styles: the debug panel owns those once
 * a career has started, and a reload has to give them back what they picked
 * rather than what the file says. Then `book`, which reads `campaign.next` and
 * would otherwise offer this session's opponent against last session's rank.
 */
/**
 * The roster baseline, and then nothing until somebody opens the front door.
 *
 * `dressThePlayer` still runs first for the reason it always has: a career that
 * has never been saved needs somebody in the slot, and the roster entry is the
 * only answer there is before a save has been read. What changed at step 66 is
 * what comes after it — the game used to load whatever was in `localStorage`
 * and put you on the marks, which works exactly as long as nobody ever wants a
 * second career.
 */
dressThePlayer();
wearWhatWasBought();
// Held from the first frame. The ladder churns and the train runs behind the
// title screen; only the round waits.
match.hold(true);

/**
 * Past the front end and into a career, whichever door was used.
 *
 * `match` starts *in* `intro` rather than entering it, so no phase change ever
 * fires for the opening round — which is why `book` is called here rather than
 * left to the listener.
 */
function beginCareer() {
  started = true;
  book();
  warmTheCards();
  match.hold(true);
  if (!hub.show()) walkout.show();
}

/**
 * The two doors into a career, called by the front door (`main.js`) once this
 * module has finished loading — which is to say once both corners have a body
 * and every clip has been retargeted. The title and create screens are the
 * front door's and were up long before this module existed (step 96); what
 * they hand over is a decision, and this is where it is carried out.
 */
export function continueCareer() {
  loaded = store.load();
  // The save is the authority on what was bought, so the layer goes on again
  // once it has landed. Same order the boot sequence has always had: the file
  // first, then the career over the top of it (gotcha 64).
  wearWhatWasBought();
  if (import.meta.env.DEV) {
    console.info(
      loaded.ok
        ? `[career] continued from ${loaded.at ?? 'a previous session'} · ${campaign.fights} fights, rank ${campaign.rank}`
        : `[career] could not continue — ${loaded.reason}`
    );
  }
  beginCareer();
  return loaded;
}

export function newCareer({ name, styles }) {
  // Throws the old career away and writes a clean one immediately, so a
  // reload straight after does not find the one that was just discarded.
  restartCareer();
  // Onto the **roster entry**, which is what `bookFighter` dresses a fighter
  // from and what every screen reads. `dressThePlayer` then takes it down the
  // same path every other fighter on the ladder goes down — there is no
  // second way to put somebody in the ring and there must not be (gotcha 54).
  ladder.identify(ladder.playerId, { name, styles });
  dressThePlayer();
  wearWhatWasBought();
  store.save();
  if (import.meta.env.DEV) {
    console.info(`[career] ${name} starts at rank ${campaign.rank} · ${styles.join(' + ')}`);
  }
  beginCareer();
}


/**
 * The read-out the fight actually has.
 *
 * Everything on it was already in the debug overlay; this is the same data read
 * the way a player reads it. It shows and hides itself off `match.live`, so
 * nothing has to remember to get it out of a screen's way.
 */
const hud = createHud({ player, opponent, match });

/**
 * The one screen that interrupts a round rather than filling a gap.
 *
 * Which makes it the one that has to stop something: `paused` is read by
 * `step` and skips `advance` entirely, so the round clock, the ladder's churn,
 * the train and the crowd hold still together. A pause that let any of them
 * keep running would be a pause that cost you something.
 */
let paused = false;
const pause = createPause({
  player,
  match,
  audio,
  onResume: () => {
    paused = false;
  },
});

const overlay = createDebugOverlay({
  combatant: player,
  opponent,
  fight,
  input,
  ai,
  impact,
  match,
  arenas,
  crowd,
  campaign,
  audio,
  career: { store, restart: restartCareer },
});

/**
 * One frame of the *fight*: everything with a rule in it, and nothing drawn.
 *
 * Split out from `step` rather than copied, so a headless run and the game play
 * out in the same order. A verification harness that assembled its own frame
 * order would be verifying its own frame order.
 */
function advance(dt) {
  // Real time, always: the freeze has to be able to end itself, and the shake
  // carries on rattling while the fighters are held still.
  impact.update(dt);

  // The round's own clock runs through a hitstop — a freeze is a camera trick,
  // not time stopping — but nothing else does.
  match.update(dt);

  // And so does everybody else's night. The ladder runs on the same clock the
  // fight does, so a long round is one in which other people's results come in
  // — the ladder the player climbs back onto is not the one they left.
  ladder.update(dt);

  // Before the freeze check, and deliberately. A service runs whether or not
  // anybody is fighting, and it keeps running through a hitstop — a train does
  // not stop for a good punch. It is the same reasoning as the round clock.
  arenas.update(dt);
  // The crowd's own business: watching for somebody hitting the floor, and
  // deciding whether to reach over the barrier. Outside the freeze with the
  // arena, because a hitstop is a camera trick and the room does not stop.
  crowd.update(dt);
  // And the floor. After the crowd, so a weapon thrown in this frame is
  // already there to be walked onto rather than a frame behind.
  ground.update(dt, [player, opponent]);
  drops.update(dt);
  // After the crowd, whose energy is the only thing in here that is not an
  // event. Outside the freeze with them, for the same reason: a hitstop is a
  // camera trick and the room does not stop.
  audio.update(dt);

  if (!impact.frozen) {
    // Polled once, at the top of the frame, so every consumer downstream agrees
    // on what was pressed this frame rather than racing the event queue.
    //
    // Skipped entirely during hitstop rather than polled and discarded. The
    // input layer already holds a key pressed since the last poll in a sticky
    // set, so a jab thrown into the freeze is not lost — it is delivered whole
    // on the first frame after it.
    input.update();
    // Intent before consequence: the controls decide what each fighter is
    // trying to do, and only then does the fight pose them and resolve what
    // landed.
    //
    // Only while the round is live. On the marks and after a finish the
    // fighters are still posed and still rendered — they are just nobody's to
    // drive.
    if (match.live) {
      // The player's hands, unless something else has them.
      (autopilot ?? control).update(dt);
      ai.update(dt);
    }
    fight.update(dt);
  }
}

/** One frame, drawn. Named so a headless test can drive it with a fixed dt. */
function step(dt) {
  // Held, not slowed. The one place in the project where `advance` does not run
  // — see `pause.js` for why it is all of it or none of it.
  if (pause.up) paused = true;
  if (!paused) advance(dt);

  // Derived every frame rather than set once: the moment a fighter can move,
  // the framing has to follow them, and a camera that only works at the
  // starting positions is a bug waiting for the first step.
  // Real time, like the shake: the camera does not stop for a hitstop, and
  // it does not stop for the pause screen's frozen fight either — but nobody
  // can see that one.
  if (!paused) director.update(dt);
  const shot = director.shot(fight.combatants.map((c) => c.position));
  frameCamera(
    stage.camera,
    shot.points,
    // `side` keeps the shot on the track side of the platform. Without it the
    // camera flips to behind the tiled wall the moment the fighters walk past
    // each other, because which side is "the side" is decided by which of them
    // is currently on the left.
    // The arena's own framing: the side lock, and how far back a platform has
    // to be read from. A boxing ring is watched from three metres; a station is
    // not, and the difference is data rather than a constant in here.
    {
      shake: impact.offset,
      drift: shot.drift,
      look: shot.look,
      push: shot.push,
      aim: shot.aim,
      ...(arenas.current?.camera ?? {}),
    }
  );
  // Drawn, not ruled: a HUD updated inside `advance` would be one whose numbers
  // lag the hitstop they are describing.
  hud.update();
  overlay.update();
  dressTheRoom();
  stage.render();
}

/**
 * The part of the picture an arena sets but no rule reads (step 98): the
 * bloom, grade and vignette, from the arena's `look`, applied when the arena
 * changes.
 */
let dressedFor = null;
function dressTheRoom() {
  const arena = arenas.current;
  if (arena === dressedFor) return;
  dressedFor = arena;
  stage.post.configure(arena?.config?.look ?? null);
}

const loop = createLoop(step);
loop.start();

// Handle for the debug overlay and for headless smoke tests.
window.__FIGHTGAME__ = {
  stage,
  loop,
  step,
  /** One frame with nothing drawn, for a headless run that wants speed. */
  advance,
  /** Hands the player's side to an AI at the given tier, or back. */
  autopilot: setAutopilot,
  fight,
  input,
  control,
  ai,
  impact,
  match,
  arenas,
  crowd,
  ground,
  ladder,
  campaign,
  /** Both currencies, what they bought, and the shop they came out of. */
  bank,
  /** The mixer, its master, and what it last played. */
  audio,
  /**
   * Dresses the opponent's slot as any fighter on the ladder, whoever the
   * campaign was actually pointing at.
   *
   * A debug affordance, like `autopilot` and the tier pin — a headless run
   * comparing two named fighters cannot wait for the ladder to offer them. It
   * goes through `bookFighter` rather than setting four things itself, because
   * a second way to dress a fighter is the thing `booking.js` exists to stop
   * (gotcha 54).
   */
  bookAs: (id, { pin: tier = pin } = {}) =>
    bookFighter(ladder.find(id), { fighter: opponent, brain: ai, pin: tier }),

  /**
   * Who is actually standing in the opponent's slot, against who ought to be.
   *
   * Written after the front door (step 66) broke both headless harnesses
   * *silently*: nothing is booked until somebody comes through the title
   * screen, so a tool that skipped it spent every round fighting an undressed
   * slot literally named "opponent" while the campaign said Fitz Halloran. The
   * rounds ran, the report printed, and the numbers were about the wrong
   * fighters.
   *
   * The lesson is not "remember to call `begin`" — it is that a harness which
   * can only fail by producing *plausible* output needs something to assert
   * against. This is that something, and `tools/` refuses to run without it.
   */
  /**
   * The colour each corner is wearing, as hex numbers. The two are the same
   * mesh, so this is what tells them apart; a harness checks they differ.
   */
  get tints() {
    return { player: player.tint, opponent: opponent.tint };
  },

  get booked() {
    const next = campaign.next.opponent ?? null;
    const slot = Object.freeze({
      name: opponent.name,
      styles: [...opponent.loadout.ids],
      tier: ai.tier,
      ringcraft: ai.room,
      /** The mesh file the slot is drawn in right now. */
      body: opponent.wearing?.split('/').pop() ?? null,
    });
    const expected = next
      ? Object.freeze({
          name: next.name,
          styles: [...next.styles],
          tier: pin ?? next.tier,
          ringcraft: next.ringcraft ?? null,
          body: next.mesh ?? null,
        })
      : null;
    return Object.freeze({
      slot,
      expected,
      started,
      /** Whether the slot is wearing the entry the campaign is pointing at. */
      dressed: Boolean(
        expected &&
          slot.name === expected.name &&
          slot.styles.join() === expected.styles.join() &&
          slot.tier === expected.tier
      ),
      /**
       * Whether the slot is in the booked fighter's body. Separate from
       * `dressed` because a body arrives asynchronously — `opponent.bodyReady`
       * resolves when it has — and a harness that asserts `dressed` straight
       * after booking must not fail on a load still in flight.
       */
      bodied: Boolean(expected && slot.body === expected.body),
    });
  },
  /** The screens, so a headless run can see and skip them. */
  // The title and create screens are the front door's (`main.js`), up before
  // this module loaded; listed here so a harness finds every screen in one place.
  screens: { title: front.title, create: front.create, walkout, aftermath, hub, hud, pause },
  /** The career: what is saved, and the one deliberate way to throw it away. */
  career: {
    store,
    restart: restartCareer,
    /**
     * Past the front end and into a career, without pressing anything.
     *
     * The door the headless harnesses go through. Before the title screen
     * existed (step 66) they did not need one: boot loaded a career and booked
     * a round, so `match.reset()` and `match.start()` were enough. Now nothing
     * is booked until somebody comes through the front, and a harness that
     * skipped it was fighting an undressed slot called "opponent" while the
     * campaign said Fitz Halloran — the rounds still ran, which is what made it
     * worth catching: it measured the wrong fighters quietly.
     */
    begin: () => {
      if (!started) beginCareer();
      return started;
    },
    /** A getter: the load happens when the title screen is answered, not at boot. */
    get loaded() {
      return loaded;
    },
  },
  /** The weapon factory, for the debug overlay and for headless checks. */
  weapons: { createWeapon, weaponIds },
  /**
   * The AI factory and its ladder, so a headless run can build a fighter at any
   * tier and put two of them in with each other. Exposed the same way the
   * weapon factory is, and for the same reason: the alternative is a second
   * copy of the wiring that drifts from this one.
   */
  brains: { createFighterAI, difficultyFor, tierNames },
  get arena() {
    return arenas.current;
  },
  get train() {
    return arenas.current?.train ?? null;
  },
  player,
  opponent,
  overlay,
  // Kept flat as well, because the smoke tests reach for these by name. A
  // getter, because the body — and so its loader handle — changes with `wear`.
  get fighter() {
    return player.fighter;
  },
  machine: player.machine,
  health: player.health,
  animation: player.animation,
};
